import {
  APPLICATION_KEYS,
  applicationOfRole,
  type MyApplications,
  ROLE_PRIORITY,
  type SystemRole,
} from '@repo/contracts';

import { NEVER_A_DESTINATION } from '@/constants/front-door';

export interface FrontDoorTarget {
  url: string;
  /** The role the person should be acting as when they arrive, so the application does not refuse them. */
  role: SystemRole;
}

const asSystemRole = (name: string): SystemRole | undefined =>
  ROLE_PRIORITY.find((role) => role === name);

/** Where an application is served as seen from this page, or null when it is not deployed there. */
export type UrlOfApplication = (applicationKey: string) => string | null;

const targetFor = (roleName: string | null, urlOf: UrlOfApplication): FrontDoorTarget | null => {
  const role = roleName ? asSystemRole(roleName) : undefined;
  const key = role ? applicationOfRole(role)?.key : undefined;

  if (!role || !key || NEVER_A_DESTINATION.includes(key)) return null;

  const url = urlOf(key);

  return url ? { url, role } : null;
};

/**
 * Where a person with nothing to do in the platform goes, and as which role: their
 * default role's application, else their highest-priority role's. Null means show the
 * no-access page.
 */
export const frontDoorTarget = (
  mine: MyApplications,
  urlOf: UrlOfApplication,
): FrontDoorTarget | null => {
  const fromDefault = targetFor(mine.defaultRole, urlOf);

  if (fromDefault) return fromDefault;

  const held = mine.applications.flatMap((application) => application.roles);

  for (const role of ROLE_PRIORITY) {
    if (!held.includes(role) || applicationOfRole(role)?.key === APPLICATION_KEYS.training) {
      continue;
    }

    const target = targetFor(role, urlOf);

    if (target) return target;
  }

  return null;
};
